<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import ReportsAPI from 'dashboard/api/reports';
import SummaryReportsAPI from 'dashboard/api/summaryReports';
import { GROUP_BY_FILTER } from './constants';
import BarChart from 'shared/components/charts/BarChart.vue';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const { t } = useI18n();
const store = useStore();

const from = ref(0);
const to = ref(0);
const groupBy = ref(GROUP_BY_FILTER[1]);
const selectedInboxId = ref(null);
const isLoading = ref(false);

const metricMap = {
  complete: 'case_close_complete_count',
  noResponse: 'case_close_no_response_count',
  promoComplaint: 'case_close_promo_complaint_count',
  contactOtherParty: 'case_close_contact_other_party_count',
};

const typeLabels = computed(() => ({
  complete: t('CONVERSATION.CASE_CLOSING.TYPES.COMPLETE'),
  noResponse: t('CONVERSATION.CASE_CLOSING.TYPES.NO_RESPONSE'),
  promoComplaint: t('CONVERSATION.CASE_CLOSING.TYPES.PROMO_COMPLAINT'),
  contactOtherParty: t('CONVERSATION.CASE_CLOSING.TYPES.CONTACT_OTHER_PARTY'),
}));

const series = ref({
  complete: [],
  noResponse: [],
  promoComplaint: [],
  contactOtherParty: [],
});
const agentBreakdown = ref([]);

const reportScope = computed(() =>
  selectedInboxId.value
    ? { type: 'inbox', id: selectedInboxId.value }
    : { type: 'account', id: undefined }
);

const timestamps = computed(() => {
  const all = new Set();
  Object.values(series.value).forEach(items => {
    (items || []).forEach(p => all.add(Number(p.timestamp)));
  });
  return Array.from(all).sort((a, b) => a - b);
});

const valueAt = (items, ts) => {
  const point = (items || []).find(p => Number(p.timestamp) === Number(ts));
  return Number(point?.value || 0);
};

const dailyRows = computed(() =>
  timestamps.value.map(ts => {
    const complete = valueAt(series.value.complete, ts);
    const noResponse = valueAt(series.value.noResponse, ts);
    const promoComplaint = valueAt(series.value.promoComplaint, ts);
    const contactOtherParty = valueAt(series.value.contactOtherParty, ts);
    const candidates = [
      { key: 'complete', value: complete },
      { key: 'noResponse', value: noResponse },
      { key: 'promoComplaint', value: promoComplaint },
      { key: 'contactOtherParty', value: contactOtherParty },
    ];
    const top = candidates.reduce(
      (a, c) => (c.value > a.value ? c : a),
      candidates[0]
    );
    return {
      timestamp: ts,
      dateLabel: new Date(ts * 1000).toLocaleDateString(),
      complete,
      noResponse,
      promoComplaint,
      contactOtherParty,
      topType: top.value > 0 ? top.key : null,
    };
  })
);

const totals = computed(() =>
  dailyRows.value.reduce(
    (acc, row) => ({
      complete: acc.complete + row.complete,
      noResponse: acc.noResponse + row.noResponse,
      promoComplaint: acc.promoComplaint + row.promoComplaint,
      contactOtherParty: acc.contactOtherParty + row.contactOtherParty,
    }),
    { complete: 0, noResponse: 0, promoComplaint: 0, contactOtherParty: 0 }
  )
);

const totalAll = computed(() => {
  const v = totals.value;
  return v.complete + v.noResponse + v.promoComplaint + v.contactOtherParty;
});

const chartCollection = computed(() => ({
  labels: [
    typeLabels.value.complete,
    typeLabels.value.noResponse,
    typeLabels.value.promoComplaint,
    typeLabels.value.contactOtherParty,
  ],
  datasets: [
    {
      label: 'Count',
      data: [
        totals.value.complete,
        totals.value.noResponse,
        totals.value.promoComplaint,
        totals.value.contactOtherParty,
      ],
      backgroundColor: ['#0076f5', '#f59e0b', '#dc2626', '#8b5cf6'],
      borderRadius: 6,
      categoryPercentage: 0.6,
      barPercentage: 0.9,
    },
  ],
}));

const chartOptions = {
  plugins: { legend: { display: false } },
  scales: {
    x: { grid: { drawOnChartArea: false } },
    y: {
      beginAtZero: true,
      ticks: { callback: v => Number(v).toLocaleString() },
      grid: { drawOnChartArea: false },
    },
  },
};

const agents = computed(() => store.getters['agents/getAgents'] || []);
const agentNameById = computed(() => {
  const map = new Map();
  agents.value.forEach(a => map.set(Number(a.id), a.name));
  return map;
});

const fetchTimeseries = async metric => {
  const { type, id } = reportScope.value;
  const response = await ReportsAPI.getReports({
    metric,
    from: from.value,
    to: to.value,
    type,
    id,
    groupBy: groupBy.value?.period,
    businessHours: false,
    excludeIssueTime: false,
  });
  return response.data || [];
};

const fetchAgentBreakdown = async () => {
  try {
    await store.dispatch('agents/get');
  } catch {
    /* noop */
  }
  const response = await SummaryReportsAPI.getAgentReports({
    since: from.value,
    until: to.value,
    businessHours: false,
    excludeIssueTime: false,
    inboxId: selectedInboxId.value,
  });
  agentBreakdown.value = (response.data || [])
    .map(r => ({
      id: r.id,
      name: agentNameById.value.get(Number(r.id)) || `#${r.id}`,
      complete: Number(r.case_close_complete_count || 0),
      noResponse: Number(r.case_close_no_response_count || 0),
      promoComplaint: Number(r.case_close_promo_complaint_count || 0),
      contactOtherParty: Number(r.case_close_contact_other_party_count || 0),
      total:
        Number(r.case_close_complete_count || 0) +
        Number(r.case_close_no_response_count || 0) +
        Number(r.case_close_promo_complaint_count || 0) +
        Number(r.case_close_contact_other_party_count || 0),
    }))
    .sort((a, b) => b.total - a.total);
};

const fetchAll = async () => {
  if (!from.value || !to.value) return;
  isLoading.value = true;
  try {
    const [complete, noResponse, promoComplaint, contactOtherParty] =
      await Promise.all([
        fetchTimeseries(metricMap.complete),
        fetchTimeseries(metricMap.noResponse),
        fetchTimeseries(metricMap.promoComplaint),
        fetchTimeseries(metricMap.contactOtherParty),
      ]);
    series.value = { complete, noResponse, promoComplaint, contactOtherParty };
    await fetchAgentBreakdown();
  } catch {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = async ({ from: f, to: tt, selectedInbox }) => {
  from.value = f;
  to.value = tt;
  selectedInboxId.value = selectedInbox?.id || null;
  await fetchAll();
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="cct-page">
    <div class="cct-shell">
      <div class="cct-header">
        <div class="cct-header-left">
          <button
            class="cct-side-toggle"
            type="button"
            aria-label="Toggle side navigation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 4v16M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
              />
            </svg>
          </button>
          <h1 class="cct-title">Case Closing Types</h1>
        </div>
        <button class="cct-save-btn" type="button" disabled>Save view</button>
      </div>

      <div class="cct-toolbar">
        <CompactDateInboxFilters @filter-change="onFilterChange" />
      </div>

      <div class="cct-content">
        <div class="cct-kpis">
          <div class="cct-kpi-card cct-kpi-card--total">
            <div class="cct-kpi-label">Total (all types)</div>
            <div class="cct-kpi-value">{{ totalAll.toLocaleString() }}</div>
          </div>
          <div class="cct-kpi-card">
            <div class="cct-kpi-label">{{ typeLabels.complete }}</div>
            <div class="cct-kpi-value cct-kpi-value--blue">
              {{ totals.complete.toLocaleString() }}
            </div>
          </div>
          <div class="cct-kpi-card">
            <div class="cct-kpi-label">{{ typeLabels.noResponse }}</div>
            <div class="cct-kpi-value cct-kpi-value--amber">
              {{ totals.noResponse.toLocaleString() }}
            </div>
          </div>
          <div class="cct-kpi-card">
            <div class="cct-kpi-label">{{ typeLabels.promoComplaint }}</div>
            <div class="cct-kpi-value cct-kpi-value--red">
              {{ totals.promoComplaint.toLocaleString() }}
            </div>
          </div>
          <div class="cct-kpi-card">
            <div class="cct-kpi-label">{{ typeLabels.contactOtherParty }}</div>
            <div class="cct-kpi-value cct-kpi-value--purple">
              {{ totals.contactOtherParty.toLocaleString() }}
            </div>
          </div>
        </div>

        <section class="cct-card">
          <div class="cct-card-header">
            <div class="cct-card-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 3v18h18M7 14l3-3 3 2 4-5"
                />
              </svg>
              <span>Distribution by closing type</span>
            </div>
          </div>
          <div class="cct-chart-body">
            <div v-if="isLoading" class="cct-chart-empty">
              <div class="cct-spinner" />
            </div>
            <div v-else-if="totalAll === 0" class="cct-chart-empty">
              No data for this period. Select a date range above.
            </div>
            <BarChart
              v-else
              :collection="chartCollection"
              :chart-options="chartOptions"
            />
          </div>
        </section>

        <section class="cct-card">
          <div class="cct-card-header">
            <div class="cct-card-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 7h18M3 12h18M3 17h18"
                />
              </svg>
              <span>Daily breakdown</span>
            </div>
          </div>
          <div class="cct-table-wrap">
            <table class="cct-tbl">
              <thead>
                <tr>
                  <th class="cct-th cct-th--left">Date</th>
                  <th class="cct-th">{{ typeLabels.complete }}</th>
                  <th class="cct-th">{{ typeLabels.noResponse }}</th>
                  <th class="cct-th">{{ typeLabels.promoComplaint }}</th>
                  <th class="cct-th">{{ typeLabels.contactOtherParty }}</th>
                  <th class="cct-th">Top type</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoading">
                  <td colspan="6" class="cct-td cct-td--empty">
                    <div class="cct-spinner" />
                  </td>
                </tr>
                <tr v-else-if="!dailyRows.length">
                  <td colspan="6" class="cct-td cct-td--empty">
                    No data for this period
                  </td>
                </tr>
                <tr
                  v-for="row in dailyRows"
                  :key="row.timestamp"
                  class="cct-row"
                >
                  <td class="cct-td cct-td--date">{{ row.dateLabel }}</td>
                  <td class="cct-td cct-td--num">{{ row.complete }}</td>
                  <td class="cct-td cct-td--num">{{ row.noResponse }}</td>
                  <td class="cct-td cct-td--num">{{ row.promoComplaint }}</td>
                  <td class="cct-td cct-td--num">
                    {{ row.contactOtherParty }}
                  </td>
                  <td class="cct-td cct-td--date">
                    <span v-if="row.topType" class="cct-top-badge">{{
                      typeLabels[row.topType]
                    }}</span>
                    <span v-else class="cct-muted">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="cct-card">
          <div class="cct-card-header">
            <div class="cct-card-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
                />
              </svg>
              <span>Agent breakdown</span>
            </div>
          </div>
          <div class="cct-table-wrap">
            <table class="cct-tbl">
              <thead>
                <tr>
                  <th class="cct-th cct-th--left">Agent</th>
                  <th class="cct-th">{{ typeLabels.complete }}</th>
                  <th class="cct-th">{{ typeLabels.noResponse }}</th>
                  <th class="cct-th">{{ typeLabels.promoComplaint }}</th>
                  <th class="cct-th">{{ typeLabels.contactOtherParty }}</th>
                  <th class="cct-th">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoading">
                  <td colspan="6" class="cct-td cct-td--empty">
                    <div class="cct-spinner" />
                  </td>
                </tr>
                <tr v-else-if="!agentBreakdown.length">
                  <td colspan="6" class="cct-td cct-td--empty">
                    No data for this period
                  </td>
                </tr>
                <tr v-for="row in agentBreakdown" :key="row.id" class="cct-row">
                  <td class="cct-td cct-td--name">{{ row.name }}</td>
                  <td class="cct-td cct-td--num">{{ row.complete }}</td>
                  <td class="cct-td cct-td--num">{{ row.noResponse }}</td>
                  <td class="cct-td cct-td--num">{{ row.promoComplaint }}</td>
                  <td class="cct-td cct-td--num">
                    {{ row.contactOtherParty }}
                  </td>
                  <td class="cct-td cct-td--num cct-td--bold">
                    {{ row.total }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.cct-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.cct-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.cct-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.cct-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cct-side-toggle {
  width: 40px;
  height: 40px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #111827;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.cct-side-toggle:hover {
  background: #f3f4f6;
}

.cct-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.cct-save-btn {
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #ffffff;
  color: #9ca3af;
  font-size: 13px;
  font-weight: 600;
  cursor: not-allowed;
}

.cct-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
}

.cct-content {
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── KPI cards ── */
.cct-kpis {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
}

.cct-kpi-card {
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #ebedf0;
  background: #f3f4f6;
}

.cct-kpi-card--total {
  background: #111827;
  border-color: #111827;
}

.cct-kpi-card--total .cct-kpi-label {
  color: rgba(255, 255, 255, 0.7);
}
.cct-kpi-card--total .cct-kpi-value {
  color: #ffffff;
}

.cct-kpi-label {
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  line-height: 1.3;
}

.cct-kpi-value {
  margin-top: 8px;
  font-size: 28px;
  line-height: 1.05;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.03em;
}

.cct-kpi-value--blue {
  color: #0076f5;
}
.cct-kpi-value--amber {
  color: #d97706;
}
.cct-kpi-value--red {
  color: #dc2626;
}
.cct-kpi-value--purple {
  color: #7c3aed;
}

/* ── Section cards ── */
.cct-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  overflow: hidden;
}

.cct-card-header {
  min-height: 56px;
  padding: 0 16px;
  border-bottom: 1px solid #dfe3e8;
  display: flex;
  align-items: center;
}

.cct-card-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

/* ── Chart ── */
.cct-chart-body {
  height: 260px;
  padding: 10px 16px 16px;
}

.cct-chart-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

/* ── Table ── */
.cct-table-wrap {
  overflow-x: auto;
}

.cct-tbl {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  font-size: 13px;
}

.cct-th {
  padding: 10px 16px;
  text-align: center;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 600;
  color: #111827 !important;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc !important;
  text-transform: none !important;
}

.cct-th--left {
  text-align: left;
}

.cct-td {
  padding: 10px 16px;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;
  color: #374151;
  vertical-align: middle;
}

.cct-td--date {
  white-space: nowrap;
  font-weight: 500;
  color: #111827;
}
.cct-td--name {
  font-weight: 600;
  color: #111827;
}
.cct-td--num {
  text-align: center;
  font-weight: 600;
  color: #111827;
}
.cct-td--bold {
  font-weight: 700;
}
.cct-td--empty {
  text-align: center;
  padding: 28px;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cct-row:last-child .cct-td {
  border-bottom: 0;
}
.cct-row:hover .cct-td {
  background: #fafafa;
}

.cct-top-badge {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.cct-muted {
  color: #9ca3af;
}

/* ── Spinner ── */
.cct-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: cct-spin 0.8s linear infinite;
  margin: 0 auto;
}

@keyframes cct-spin {
  to {
    transform: rotate(360deg);
  }
}

::global(body.dark) .cct-page,
::global(body.dark) .cct-shell,
::global(body.dark) .cct-header,
::global(body.dark) .cct-toolbar,
::global(body.dark) .cct-card,
::global(body.theme-mix) .cct-page,
::global(body.theme-mix) .cct-shell,
::global(body.theme-mix) .cct-header,
::global(body.theme-mix) .cct-toolbar,
::global(body.theme-mix) .cct-card {
  background: #ffffff !important;
  color: #111827 !important;
}

@media (max-width: 1200px) {
  .cct-kpis {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1100px) {
  .cct-content {
    padding: 12px 16px 16px;
  }
  .cct-toolbar {
    padding: 10px 16px 8px;
  }
}

@media (max-width: 768px) {
  .cct-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
